import { TeachingLoadReportType } from '@bp/planung-graphql-types';
import { Checkbox, Grid, GridColumn, GridRow, ModalBottomButtons, Select, SelectOptionType } from '@bp/ui-components';
import { Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import { Form } from 'react-router-dom';
import { useMemorizedCacheTag } from '../../../hooks/useMemorizedCacheTag';
import {
  use_SaveTeachingLoadReportConfigMutation,
  use_TeachingLoadReportConfigQuery,
} from '../../../types/planung-graphql-client-defs';
import { showErrorToast, showSuccessUpdateToast } from '../../../utils/toast';

export type TeachingLoadReportConfigurationFormType = {
  noAdditionalTeachingLoad: boolean;
  hidePaidTeachingLoad: boolean;
  roundTeachingLoadResult: number;
};

/** Muss zum Backend-Default in `defaultTeachingLoadReportConfig` passen. */
export const teachingLoadReportConfigDefaults: TeachingLoadReportConfigurationFormType = {
  noAdditionalTeachingLoad: false,
  hidePaidTeachingLoad: false,
  roundTeachingLoadResult: 2,
};

type TeachingLoadReportConfigurationFormProps = {
  subType: TeachingLoadReportType;
  onLoad: (loading: boolean) => void;
  onClose: () => void;
};

export const TeachingLoadReportConfigurationForm = ({
  subType,
  onLoad,
  onClose,
}: TeachingLoadReportConfigurationFormProps) => {
  const { t } = useTranslation();
  const context = useMemorizedCacheTag('TEACHING_LOAD_REPORT_CONFIG');

  const [, saveConfig] = use_SaveTeachingLoadReportConfigMutation();

  const [{ data }] = use_TeachingLoadReportConfigQuery({
    variables: { type: subType },
    context,
    pause: !subType,
  });

  const current = data?.teachingLoadReportConfig;

  // count: 0 fiele im Deutschen auf die "other"-Form ("0 Nachkommastellen") – daher eigener Key.
  const decimalOptions: SelectOptionType[] = [0, 1, 2].map((decimals) => ({
    label:
      decimals === 0
        ? t('deputate.configuration.decimalsNone')
        : t('deputate.configuration.decimals', { count: decimals }),
    value: decimals,
  }));

  const initialValues: TeachingLoadReportConfigurationFormType = {
    noAdditionalTeachingLoad:
      current?.noAdditionalTeachingLoad ?? teachingLoadReportConfigDefaults.noAdditionalTeachingLoad,
    hidePaidTeachingLoad: current?.hidePaidTeachingLoad ?? teachingLoadReportConfigDefaults.hidePaidTeachingLoad,
    roundTeachingLoadResult:
      current?.roundTeachingLoadResult ?? teachingLoadReportConfigDefaults.roundTeachingLoadResult,
  };

  async function handleSubmit(values: TeachingLoadReportConfigurationFormType) {
    onLoad(true);

    const input = {
      noAdditionalTeachingLoad: values.noAdditionalTeachingLoad,
      hidePaidTeachingLoad: values.hidePaidTeachingLoad,
      roundTeachingLoadResult: values.roundTeachingLoadResult,
    };

    // Je Berichtstyp gibt es höchstens eine Einstellung; das Backend legt sie an oder ersetzt sie.
    const result = await saveConfig({ type: subType, input }, context);

    if (result.error) {
      showErrorToast(result.error);
    } else {
      showSuccessUpdateToast();
    }

    onLoad(false);
    onClose();
  }

  return (
    <Formik<TeachingLoadReportConfigurationFormType>
      initialValues={initialValues}
      enableReinitialize
      onSubmit={handleSubmit}
    >
      {({ values, setFieldValue, resetForm }) => {
        return (
          <Form>
            <Grid useFormGap>
              <GridRow spacingTop='none' spacingBottom='none'>
                <GridColumn width={12}>
                  <Checkbox
                    name='hide-paid-teaching-load'
                    checked={values.hidePaidTeachingLoad}
                    onChange={async (event) => {
                      await setFieldValue('hidePaidTeachingLoad', event.target.checked);
                    }}
                    label={t('deputate.configuration.hidePaidTeachingLoad')}
                    tooltipText={t('deputate.configuration.hidePaidTeachingLoadHint')}
                  />
                  <Checkbox
                    name='no-additional-teaching-load'
                    // hidePaidTeachingLoad erzwingt das Verhalten bereits im Backend
                    checked={values.hidePaidTeachingLoad || values.noAdditionalTeachingLoad}
                    disabled={values.hidePaidTeachingLoad}
                    onChange={async (event) => await setFieldValue('noAdditionalTeachingLoad', event.target.checked)}
                    label={t('deputate.configuration.noAdditionalTeachingLoad')}
                    tooltipText={t('deputate.configuration.noAdditionalTeachingLoadHint')}
                  />
                </GridColumn>
              </GridRow>

              <GridRow spacingTop='none' spacingBottom='none'>
                <GridColumn width={6}>
                  <Select
                    name='round-teaching-load-result'
                    options={decimalOptions}
                    value={decimalOptions.find((o) => o.value === values.roundTeachingLoadResult)}
                    onChange={async (option) =>
                      await setFieldValue('roundTeachingLoadResult', (option as SelectOptionType).value as number)
                    }
                    label={t('deputate.configuration.roundTeachingLoadResult')}
                    tooltipText={t('deputate.configuration.roundTeachingLoadResultHint')}
                  />
                </GridColumn>
              </GridRow>
            </Grid>

            <ModalBottomButtons
              closeButton={{
                callback: () => {
                  resetForm();
                  onClose();
                },
              }}
              submitButton={{
                text: t('common.save'),
                callback: async () => {
                  await handleSubmit(values);
                  resetForm();
                },
              }}
            />
          </Form>
        );
      }}
    </Formik>
  );
};
